'use client';

import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cx } from 'class-variance-authority';
import get from 'lodash/get';
import head from 'lodash/head';
import { useState } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import { Button } from '~/components/ui/Button';
import StyledInput, { type StyledInputProps } from '~/components/ui/Form/StyledInput';
import { EmailSuggestionMessage } from '../EmailSuggestionMessage';

/**
 * The plain text field of `FormInput`, on its own.
 *
 * `FormInput` is a compound component: `FormInput.DatePicker`, `.Code`, `.IBAN`,
 * `.Dropzone` and a dozen more hang off it, so importing it for a name field imports
 * all of them — about 440 KB of JavaScript on a club website whose contact form has
 * four text fields. The public widgets import this instead; `FormInput` itself is this
 * with its sub-components attached, unchanged for the app.
 */
export interface FormTextInputProps extends StyledInputProps {
    name: string;
    options?: RegisterOptions;
    hideError?: boolean;
    /** @deprecated */
    withoutShifting?: boolean;
    secure?: boolean;
    transform?: (value: undefined | null | string) => undefined | null | string;
}

const FormTextInput = ({
    name,
    onBlur,
    className,
    onChange,
    options,
    // Taken out of `props` so they never reach the <input>; neither is used any more.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    withoutShifting,
    hideError,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    transform,
    secure,
    ...props
}: FormTextInputProps) => {
    const {
        register,
        formState: { errors }
    } = useFormContext();

    const [isLocked, setIsLocked] = useState(secure);
    const errorObj = get(errors, name);
    const errorMessage = (head(errorObj) || errorObj)?.message;
    const methods = register(name, { onBlur, onChange, ...options });

    return (
        <StyledInput
            contentRight={
                secure ? (
                    <Button
                        onPress={() => setIsLocked(!isLocked)}
                        size="xs"
                        color={isLocked ? 'warning' : 'success'}
                        icon={<FontAwesomeIcon icon={isLocked ? faLock : faLockOpen} />}
                    />
                ) : undefined
            }
            {...props}
            disabled={(secure && isLocked) || props.disabled}
            name={name}
            className={cx(className, !props.flush && 'tw-my-3')}
            helperText={
                (!hideError && errorMessage ? (
                    <EmailSuggestionMessage
                        name={name}
                        message={errorMessage}
                        onAccept={(value) => void methods.onChange({ target: { name, value }, type: 'change' })}
                    />
                ) : undefined) || props.helperText
            }
            isInvalid={!!errorMessage}
            {...methods}
        />
    );
};

export default FormTextInput;
